import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../shared/lib/apiClient';
import quickbooksLogo from '../../../shared/assets/logos/integrations/quickbooks.svg';
import squareLogo from '../../../shared/assets/logos/integrations/square.svg';
import stripeLogo from '../../../shared/assets/logos/integrations/stripe.svg';
import gustoLogo from '../../../shared/assets/logos/integrations/gusto.svg';
import shopifyLogo from '../../../shared/assets/logos/integrations/shopify.svg';
import hubspotLogo from '../../../shared/assets/logos/integrations/hubspot.svg';

const PROVIDER_META = {
  quickbooks: { name: 'QuickBooks', logo: quickbooksLogo },
  square: { name: 'Square', logo: squareLogo },
  stripe: { name: 'Stripe', logo: stripeLogo },
  gusto: { name: 'Gusto', logo: gustoLogo },
  shopify: { name: 'Shopify', logo: shopifyLogo },
  hubspot: { name: 'HubSpot', logo: hubspotLogo },
};

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const IntegrationStatus = ({ loaded, onNavigateToIntegrations }) => {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchIntegrations() {
      try {
        const response = await apiFetch('/api/integrations');
        const data = await response.json();
        if (data.integrations) {
          setIntegrations(data.integrations.map(int => {
            const meta = PROVIDER_META[int.provider] || { name: int.provider, logo: null };
            return {
              id: int.provider,
              name: meta.name,
              logo: meta.logo,
              status: int.status === 'active' ? 'synced' : 'disconnected',
              lastSync: timeAgo(int.connected_at),
            };
          }));
        }
      } catch {
        // silently fail — show empty state
      } finally {
        setLoading(false);
      }
    }
    fetchIntegrations();
  }, []);

  return (
    <div
      className="bg-white border border-zinc-100 p-5 rounded-lg h-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(16px)',
        transitionDelay: '1200ms',
      }}
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-4">
        Connected Integrations
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      ) : integrations.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-xs text-zinc-400 mb-3">No integrations connected yet</p>
          <button
            onClick={() => onNavigateToIntegrations ? onNavigateToIntegrations() : navigate('/quickbooks')}
            className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-900 transition-colors duration-300"
          >
            Connect your first integration
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          {integrations.map((int, i) => (
            <div
              key={int.id}
              className="p-3.5 border border-zinc-100 rounded-md hover:border-zinc-200 transition-all duration-300 cursor-pointer group"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                transition: 'all 0.5s cubic-bezier(0.16,1,0.3,1)',
                transitionDelay: `${1250 + i * 60}ms`,
              }}
            >
              <div className="flex items-start justify-between mb-3">
                {int.logo ? (
                  <img
                    src={int.logo}
                    alt={int.name}
                    className="w-5 h-5 opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                  />
                ) : (
                  <div className="w-5 h-5 bg-zinc-200 rounded" />
                )}
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${
                    int.status === 'synced'
                      ? 'bg-emerald-500'
                      : int.status === 'syncing'
                      ? 'bg-blue-500 animate-pulse'
                      : 'bg-zinc-300'
                  }`}
                />
              </div>
              <div className="text-xs font-medium text-zinc-900 mb-0.5">{int.name}</div>
              <div className="text-[10px] text-zinc-400">{int.lastSync}</div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => onNavigateToIntegrations ? onNavigateToIntegrations() : navigate('/quickbooks')}
        className="w-full mt-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-zinc-400 border border-zinc-100 hover:border-zinc-900 hover:text-zinc-900 rounded-md transition-colors duration-300"
      >
        + Add Integration
      </button>
    </div>
  );
};

export default IntegrationStatus;
